import api from './api';

export interface OlxConnection {
  configured: boolean;
  connected: boolean;
  account_name: string | null;
  account_email: string | null;
  zipcode: string | null;
  phone: string | null;
  connected_at: string | null;
}

export type OlxListingStatus = 'pending' | 'queued' | 'accepted' | 'refused' | 'error' | 'removing' | 'removed';

export interface OlxListing {
  id: number;
  vehicle_id: number;
  status: OlxListingStatus;
  list_id: string | null;
  url: string | null;
  message: string | null;
  published_at: string | null;
}

export interface OlxForm {
  subject: string;
  body: string;
  price: number | '';
  vehicle_brand: string;
  vehicle_model: string;
  vehicle_version: string;
  gearbox: string;
  fuel: string;
  cubiccms: string;
  car_steering: string;
  motorpower: string;
  carcolor: string;
  doors: string;
}

export interface OlxVehicleInfo {
  connection: OlxConnection;
  supported: boolean;
  category: number | null;
  missing: string[];
  defaults: Partial<OlxForm>;
  image_count: number;
  listing: OlxListing | null;
}

export interface OlxOption {
  id: string;
  name: string;
}

export const OLX_CATEGORY_MOTORCYCLE = 2060;

/** Página que recebe o retorno da OLX; precisa estar cadastrada como redirect_uri. */
export const olxRedirectUri = () => `${window.location.origin}/integracoes/olx`;

const olxService = {
  async getConnection(): Promise<OlxConnection> {
    return (await api.get<OlxConnection>('/olx/connection')).data;
  },

  async getConnectUrl(): Promise<string> {
    const response = await api.post<{ url: string }>('/olx/connect-url', { redirect_uri: olxRedirectUri() });
    return response.data.url;
  },

  async finishConnection(code: string, state: string): Promise<OlxConnection> {
    return (await api.post<OlxConnection>('/olx/callback', { code, state })).data;
  },

  async saveSettings(zipcode: string, phone: string): Promise<OlxConnection> {
    return (await api.put<OlxConnection>('/olx/settings', { zipcode, phone })).data;
  },

  async disconnect(): Promise<OlxConnection> {
    return (await api.delete<OlxConnection>('/olx/connection')).data;
  },

  async getCatalog(type: 'car' | 'moto', brand?: string, model?: string): Promise<OlxOption[]> {
    return (await api.get<OlxOption[]>('/olx/catalog', { params: { type, brand, model } })).data;
  },

  async getCubicCms(): Promise<OlxOption[]> {
    return (await api.get<OlxOption[]>('/olx/cubiccms')).data;
  },

  async getVehicleInfo(vehicleId: number | string): Promise<OlxVehicleInfo> {
    return (await api.get<OlxVehicleInfo>(`/olx/vehicles/${vehicleId}`)).data;
  },

  async publish(vehicleId: number | string, form: OlxForm): Promise<OlxListing> {
    return (await api.post<{ listing: OlxListing }>(`/olx/vehicles/${vehicleId}`, form)).data.listing;
  },

  async refresh(vehicleId: number | string): Promise<OlxListing> {
    return (await api.post<{ listing: OlxListing }>(`/olx/vehicles/${vehicleId}/refresh`)).data.listing;
  },

  async remove(vehicleId: number | string): Promise<OlxListing> {
    return (await api.delete<{ listing: OlxListing }>(`/olx/vehicles/${vehicleId}`)).data.listing;
  },
};

export default olxService;
